import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Select, Space, Table, Tag } from 'antd';
import { PlusOutlined, RobotOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useApp } from 'contexts/app';
import { useWorkSpace } from 'contexts/workspace';
import StoreSettingService from 'services/store_setting';
import ProductPicker from 'components/pickers/product';

const service = new StoreSettingService();

const LandingList = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkSpace();
  const { showLoading, showNotify, message, showConfirm } = useApp();
  const [aiForm] = Form.useForm();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ keyword: '', status: '' });
  const [showAIModal, setShowAIModal] = useState(false);

  const loadData = async () => {
    showLoading(true);
    try {
      const result = await service.getSEOLandingPages({
        status: filter.status,
        keyword: filter.keyword,
        page: 1,
        page_size: 200,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      message.error(error?.message || 'Không thể tải danh sách landing page');
    }
    showLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [filter.status]);

  const handlePublish = async (record) => {
    showLoading(true);
    try {
      await service.publishSEOLandingPage({ id: record.id });
      showNotify('Đã publish landing page');
      await loadData();
    } catch (error) {
      message.error(error?.message || 'Không thể publish landing page');
    }
    showLoading(false);
  };

  const handleUnpublish = async (record) => {
    showLoading(true);
    try {
      await service.unpublishSEOLandingPage({ id: record.id });
      showNotify('Đã chuyển landing page về nháp');
      await loadData();
    } catch (error) {
      message.error(error?.message || 'Không thể unpublish landing page');
    }
    showLoading(false);
  };

  const handleDelete = async (record) => {
    showConfirm({
      title: 'Xóa landing page',
      content: `Bạn có chắc chắn muốn xóa landing page "${record.title}"?`,
      onOk: async () => {
        showLoading(true);
        try {
          await service.deleteSEOLandingPage({ id: record.id });
          showNotify('Đã xóa landing page');
          await loadData();
        } catch (error) {
          message.error(error?.message || 'Không thể xóa landing page');
        }
        showLoading(false);
      },
    });
  };

  const handleViewLanding = (record) => {
    const slug = String(record?.slug || '').trim();
    if (!slug) {
      message.error('Landing page chưa có slug để xem bài viết');
      return;
    }

    const websiteBaseUrl = String(workspace?.websiteUrl || '').replace(/\/$/, '');
    if (!websiteBaseUrl) {
      message.error('Chưa cấu hình website shop để xem bài viết');
      return;
    }

    const landingUrl = `${websiteBaseUrl}/blog/${encodeURIComponent(slug)}`;
    window.open(landingUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEditLanding = (record) => {
    const id = String(record?.id || '').trim();
    if (!id) {
      message.error('Landing page chưa có id để chỉnh sửa');
      return;
    }

    navigate(`/seo-center/landing/edit/${encodeURIComponent(id)}`);
  };

  const openAIModal = () => {
    aiForm.setFieldsValue({
      prompt_description: '',
      product_ids: [],
    });
    setShowAIModal(true);
  };

  const handleGenerateLandingByAI = async () => {
    const values = await aiForm.validateFields();

    showLoading(true);
    try {
      const result = await service.generateSEOLandingAI({
        prompt_description: values?.prompt_description,
        product_ids: Array.isArray(values?.product_ids) ? values.product_ids : [],
      });

      const generated = result || {};
      const generatedSEO = generated?.seo || {};

      setShowAIModal(false);
      showNotify('Đã tạo bản nháp bằng AI. Bạn đang được chuyển sang trang chỉnh sửa để rà soát và lưu.');

      navigate('/seo-center/landing/new', {
        state: {
          aiGeneratedLanding: {
            title: generated?.title || '',
            slug: generated?.slug || '',
            excerpt: generated?.excerpt || String(values?.prompt_description || '').slice(0, 500),
            content_html: generated?.content_html || '',
            product_ids: Array.isArray(values?.product_ids) ? values.product_ids : [],
            status: 'draft',
            seo: {
              meta_title: generatedSEO?.meta_title || '',
              meta_description: generatedSEO?.meta_description || '',
              keywords: Array.isArray(generatedSEO?.keywords) ? generatedSEO.keywords : [],
              og_title: generatedSEO?.og_title || '',
              og_description: generatedSEO?.og_description || '',
              robots: 'index, follow',
            },
          },
        },
      });
    } catch (error) {
      message.error(error?.message || 'Không thể tạo landing page bằng AI');
    }
    showLoading(false);
  };

  const columns = [
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{record.title || '(Chưa đặt tiêu đề)'}</span>
          <span style={{ color: '#666' }}>/blog/{record.slug}</span>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 140,
      render: (status) => status === 'published' ? <Tag color="green">Published</Tag> : <Tag>Draft</Tag>,
    },
    {
      title: 'Cập nhật',
      dataIndex: 'update_on',
      width: 180,
      render: (value) => value ? new Date(value).toLocaleString('vi-VN') : '-',
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 420,
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleViewLanding(record)}>Xem bài viết</Button>
          <Button onClick={() => handleEditLanding(record)}>Sửa</Button>
          {record.status === 'published' ? (
            <Button onClick={() => handleUnpublish(record)}>Unpublish</Button>
          ) : (
            <Button type="primary" onClick={() => handlePublish(record)}>Publish</Button>
          )}
          <Button danger onClick={() => handleDelete(record)}>Xóa</Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space wrap>
          <Input.Search
            placeholder="Tìm landing theo tiêu đề hoặc slug"
            allowClear
            style={{ width: 320 }}
            onSearch={(value) => setFilter((prev) => ({ ...prev, keyword: value }))}
          />
          <Select
            value={filter.status}
            style={{ width: 180 }}
            onChange={(value) => setFilter((prev) => ({ ...prev, status: value }))}
            options={[
              { label: 'Tất cả trạng thái', value: '' },
              { label: 'Draft', value: 'draft' },
              { label: 'Published', value: 'published' },
            ]}
          />
          <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate('/seo-center/landing/new')}>
            Tạo landing page
          </Button>
          <Button icon={<RobotOutlined />} onClick={openAIModal}>
            Tạo bằng AI
          </Button>
          <Button onClick={loadData}>Tải lại</Button>
        </Space>
      </Card>

      <Card title="Danh sách landing page">
        <Table rowKey="id" columns={columns} dataSource={items} pagination={false} />
      </Card>

      <Modal
        title="Tạo landing page bằng AI"
        open={showAIModal}
        onCancel={() => setShowAIModal(false)}
        onOk={handleGenerateLandingByAI}
        okText="Tạo nháp và mở trang chỉnh sửa"
        cancelText="Hủy"
        width={760}
      >
        <Form form={aiForm} layout="vertical">
          <Form.Item
            label="Mô tả mục tiêu nội dung"
            name="prompt_description"
            rules={[{ required: true, message: 'Vui lòng nhập mô tả để AI tạo nội dung' }]}
          >
            <Input.TextArea
              rows={6}
              placeholder="Ví dụ: Landing giới thiệu giải pháp đặt hàng sỉ cho cửa hàng thực phẩm, nhấn mạnh giảm sai sót khi chốt đơn qua Zalo/Facebook và tăng tốc xử lý đơn."
            />
          </Form.Item>

          <Form.Item label="Sản phẩm muốn đưa vào bài viết" name="product_ids">
            <ProductPicker mode="multiple" placeholder="Chọn sản phẩm để AI đưa vào phần nội dung và gợi ý SEO" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default LandingList;
